import React, { useState } from 'react'
import { Button } from 'react-bootstrap'
import { useDispatch, useSelector } from 'react-redux'
import axios from 'axios'
import { selectRootUser } from 'rootSlice'
import { closeConnectionModal, fetchAllPublicKeys, fetchMcpPublicKeys } from '../../integrationsSlice'
import { baseUrl, integrationsKeysUrl } from 'urls'
import { getAxiosDefaultConfig } from 'utils'
import { toast } from 'react-toastify'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faSpinner } from '@fortawesome/free-solid-svg-icons'
import { isTeamOwner as checkIsTeamOwner } from '../../isTeamOwner'

/** Get tag value from tags array; supports both { key, value } and { Key, Value }. */
function getTagValue(tags, tagKey) {
  if (!Array.isArray(tags)) return ''
  const keyLower = String(tagKey).toLowerCase()
  const entry = tags.find(
    (t) => (t.key ?? t.Key ?? '').toLowerCase() === keyLower
  )
  return entry ? (entry.value ?? entry.Value ?? '') : ''
}

const MASKED = '••••••••••••••••••••••••••••••••'
const isStoredHash = (value) => typeof value === 'string' && value.startsWith('aws:')

export default function PayHelmMcpConnectionModalContent({ ownerId, sharedAccountIds, channel }) {
  const dispatch = useDispatch()
  const user = useSelector(selectRootUser)
  const isTeamOwner = checkIsTeamOwner(user)
  const closeModal = () => dispatch(closeConnectionModal())
  const [generating, setGenerating] = useState(false)
  const [generatedKey, setGeneratedKey] = useState(null)
  const [revealedKey, setRevealedKey] = useState(null)
  const [revealing, setRevealing] = useState(false)
  const [error, setError] = useState(null)

  const ownKeyPrefix = user?.id != null ? `${user.id}--` : ''
  const ownPpKey = ownKeyPrefix
    ? channel?.ppKeys?.find((k) => k.keyId?.startsWith(ownKeyPrefix))
    : null
  const existingKey = ownPpKey
  const hasExistingKey = !!existingKey?.keyId
  const accountId = existingKey ? getTagValue(existingKey.tags, 'ownerId') || existingKey.keyId?.split('--')[0] || '' : ''
  const keyIsStoredSecurely = existingKey?.privateKey && isStoredHash(existingKey.privateKey)
  const privateKeyDisplay = existingKey?.privateKey
    ? (keyIsStoredSecurely ? (revealedKey ?? 'Stored securely') : (revealedKey ?? MASKED))
    : ''
  const canReveal = keyIsStoredSecurely && !revealedKey
  const showRevealOrCopy = canReveal || revealedKey

  const syncKeysAfterIssue = async () => {
    const keysOwnerId = ownerId ?? user?.id
    await dispatch(fetchAllPublicKeys({ ownerId: keysOwnerId }))
    await dispatch(
      fetchMcpPublicKeys({
        ownerId,
        sharedAccountIds: sharedAccountIds || [],
        currentUserId: user?.id,
        isTeamOwner,
      })
    )
  }

  const handleGenerateKey = async () => {
    setError(null)
    setGenerating(true)
    try {
      const res = await axios.get(
        `${baseUrl}/auth/mcp-keys/link?format=json`,
        getAxiosDefaultConfig()
      )
      const data = res?.data
      if (data?.ok && data?.openai_identifier) {
        setGeneratedKey(data.openai_identifier)
        setRevealedKey(null)
        await syncKeysAfterIssue()
        toast.success('MCP key generated. Copy it now — it won’t be shown again in full.')
      } else {
        setError(data?.error || 'Failed to generate key')
      }
    } catch (err) {
      const message = err?.response?.data?.error || err?.message || 'Failed to generate MCP key'
      setError(message)
      toast.error(message)
    } finally {
      setGenerating(false)
    }
  }

  const handleRegenerateKey = async () => {
    if (
      !window.confirm(
        'Regenerate your MCP key? Clients using the old key (including Cursor, ChatGPT, etc.) will stop working until you update them with the new key.'
      )
    ) {
      return
    }
    setError(null)
    setRevealedKey(null)
    setGenerating(true)
    try {
      const res = await axios.post(
        `${baseUrl}/auth/mcp-keys/regenerate`,
        {},
        getAxiosDefaultConfig()
      )
      const data = res?.data
      if (data?.ok && data?.openai_identifier) {
        setGeneratedKey(data.openai_identifier)
        await syncKeysAfterIssue()
        toast.success('New MCP key created. Copy it now and update your MCP clients — the old key no longer works.')
      } else {
        setError(data?.error || 'Failed to regenerate key')
      }
    } catch (err) {
      const message = err?.response?.data?.error || err?.message || 'Failed to regenerate MCP key'
      setError(message)
      toast.error(message)
    } finally {
      setGenerating(false)
    }
  }

  const handleCopy = () => {
    const keyToCopy = generatedKey || revealedKey
    if (!keyToCopy) return
    navigator.clipboard.writeText(keyToCopy)
    toast.success('MCP key copied to clipboard')
  }

  const handleRevealKey = async () => {
    if (!existingKey?.keyId) return
    setError(null)
    setRevealing(true)
    try {
      const res = await axios.get(
        `${integrationsKeysUrl}/reveal-mcp?keyId=${encodeURIComponent(existingKey.keyId)}`,
        getAxiosDefaultConfig()
      )
      const key = res?.data?.key
      if (key) {
        setRevealedKey(key)
        toast.success('Key revealed. Copy and store it securely.')
      } else {
        setError('Key not returned')
      }
    } catch (err) {
      const message = err?.response?.data?.error || err?.message || 'Failed to fetch key'
      setError(message)
      toast.error(message)
    } finally {
      setRevealing(false)
    }
  }

  return (
    <div className="tw-p-6">
      <h2 className="tw-text-xl tw-mb-2">Connect PayHelm MCP</h2>
      <div className="tw-text-slate-500 tw-mb-4">
        {hasExistingKey
          ? 'Your MCP key is connected. Use Agent Mode in AI chat and Slack.'
          : 'Generate an MCP key to use Agent Mode in AI chat and Slack. For organization workspaces, generate the key while switched into that org — personal keys do not apply to org Slack channels. Store it securely; you can regenerate anytime from here.'}
      </div>

      {error && (
        <div className="tw-mb-4 tw-p-3 tw-bg-red-50 tw-text-red-700 tw-rounded tw-text-sm">
          {error}
        </div>
      )}

      {hasExistingKey && !generatedKey && (
        <div className="tw-rounded-lg tw-border tw-border-slate-200 tw-overflow-hidden tw-mb-4">
          <table className="tw-table-auto tw-w-full tw-text-sm tw-border-collapse">
            <thead className="tw-bg-slate-50 tw-border-b tw-border-slate-200">
              <tr>
                <th className="tw-px-3 tw-py-2 tw-text-left tw-font-medium tw-text-slate-600">Account ID</th>
                <th className="tw-px-3 tw-py-2 tw-text-left tw-font-medium tw-text-slate-600">Private key</th>
                {showRevealOrCopy && <th className="tw-px-3 tw-py-2 tw-w-24" />}
              </tr>
            </thead>
            <tbody>
              <tr>
                <td className="tw-px-3 tw-py-2 tw-font-mono tw-break-all">{accountId || '—'}</td>
                <td className="tw-px-3 tw-py-2 tw-font-mono tw-break-all">{privateKeyDisplay}</td>
                {showRevealOrCopy && (
                  <td className="tw-px-3 tw-py-2">
                    {canReveal ? (
                      <Button
                        variant="outline-primary"
                        size="sm"
                        onClick={handleRevealKey}
                        disabled={revealing}
                      >
                        {revealing ? (
                          <>
                            <FontAwesomeIcon icon={faSpinner} spin className="tw-mr-1" />
                            Revealing…
                          </>
                        ) : (
                          'Reveal key'
                        )}
                      </Button>
                    ) : revealedKey ? (
                      <Button variant="outline-secondary" size="sm" onClick={handleCopy}>
                        Copy key
                      </Button>
                    ) : null}
                  </td>
                )}
              </tr>
            </tbody>
          </table>
          <div className="tw-px-3 tw-py-2 tw-bg-slate-50 tw-border-t tw-border-slate-200">
            <Button
              variant="outline-danger"
              size="sm"
              onClick={handleRegenerateKey}
              disabled={generating}
            >
              {generating ? (
                <>
                  <FontAwesomeIcon icon={faSpinner} spin className="tw-mr-1" />
                  Regenerating…
                </>
              ) : (
                'Regenerate key'
              )}
            </Button>
            <span className="tw-ml-2 tw-text-xs tw-text-slate-500">
              Invalidates the current key everywhere it is configured.
            </span>
          </div>
        </div>
      )}

      {!generatedKey && !hasExistingKey ? (
        <div className="tw-flex tw-items-center tw-gap-3">
          <Button
            variant="primary"
            onClick={handleGenerateKey}
            disabled={generating}
          >
            {generating ? (
              <>
                <FontAwesomeIcon icon={faSpinner} spin className="tw-mr-2" />
                Generating…
              </>
            ) : (
              'Generate MCP key'
            )}
          </Button>
        </div>
      ) : generatedKey ? (
        <div className="tw-space-y-3">
          <div className="tw-text-sm tw-font-medium tw-text-slate-600">Your MCP key (copy and store securely):</div>
          <div className="tw-flex tw-items-center tw-gap-2 tw-p-3 tw-bg-slate-100 tw-rounded tw-font-mono tw-text-sm tw-break-all">
            {generatedKey}
          </div>
          <Button variant="secondary" onClick={handleCopy}>
            Copy key
          </Button>
        </div>
      ) : null}

      <div className="tw-mt-6 tw-pt-4 tw-border-t tw-border-slate-200 tw-flex tw-justify-end">
        <Button variant="secondary" onClick={closeModal}>
          {generatedKey || hasExistingKey ? 'Done' : 'Cancel'}
        </Button>
      </div>
    </div>
  )
}
